import i18n from 'i18next';
import './../lib/i18n';

export enum UnitSystem {
  IMPERIAL,
  METRIC
}

const GALLONS_TO_LITERS_FACTOR = 3.785412;
const PSI_TO_KPA_FACTOR = 6.89476;

const volumeUnits = {
  [UnitSystem.IMPERIAL]: {
    short: 'gal',
    long: i18n.t('Gallons')
  },
  [UnitSystem.METRIC]: {
    short: 'L',
    long: i18n.t('Liters')
  },
};

const temperatureUnits = {
  [UnitSystem.IMPERIAL]: {
    short: '°F',
    long: i18n.t('Fahrenheit')
  },
  [UnitSystem.METRIC]: {
    short: '°C',
    long: i18n.t('Celsius')
  },
};

const flowUnits = {
  [UnitSystem.IMPERIAL]: {
    short: 'gpm',
    long: i18n.t('Gallons per Minute')
  },
  [UnitSystem.METRIC]: {
    short: 'lpm',
    long: i18n.t('Liters per Minute')
  },
};

const pressureUnits = {
  [UnitSystem.IMPERIAL]: {
    short: 'PSI',
    long: i18n.t('Pounds Per Square Inch')
  },
  [UnitSystem.METRIC]: {
    short: 'kPa',
    long: i18n.t('Kilopascal')
  },
};

const humidityUnits = {
  short: '%',
  long: i18n.t('Humidity'),
}

const batteryUnits = {
  short: '%',
  long: i18n.t('Battery'),
}

export type ConverterFn = (value: number, unitSystem: UnitSystem) => number;

const convertFromGallons: ConverterFn = (gallons: number, unitSystem = UnitSystem.IMPERIAL): number => {
  return (unitSystem === UnitSystem.IMPERIAL) ? gallons : gallons * GALLONS_TO_LITERS_FACTOR;
};

const convertToGallons: ConverterFn = (litersOrGallons: number, unitSystem = UnitSystem.IMPERIAL): number => {
  return (unitSystem === UnitSystem.IMPERIAL) ? litersOrGallons : litersOrGallons / GALLONS_TO_LITERS_FACTOR;
};

const convertFromPSI: ConverterFn = (psi: number, unitSystem = UnitSystem.IMPERIAL): number => {
  return (unitSystem === UnitSystem.IMPERIAL) ? psi : psi * PSI_TO_KPA_FACTOR;
};

const convertFromFahrenheit: ConverterFn = (temp: number, unitSystem = UnitSystem.IMPERIAL): number => {
  if (unitSystem === UnitSystem.IMPERIAL) {
    return temp;
  } else {
    return temp ? (temp - 32) / 1.8 : 0;
  }
};

export type FormatterFn = (unitSystem: UnitSystem) => string;

const displayShortVolumeUnit: FormatterFn = (unitSystem: UnitSystem = UnitSystem.IMPERIAL) => volumeUnits[unitSystem].short;
const displayLongVolumeUnit: FormatterFn = (unitSystem: UnitSystem = UnitSystem.IMPERIAL) => volumeUnits[unitSystem].long;
const displayShortTempUnit: FormatterFn = (unitSystem: UnitSystem = UnitSystem.IMPERIAL) => temperatureUnits[unitSystem].short;
const displayLongTempUnit: FormatterFn = (unitSystem: UnitSystem = UnitSystem.IMPERIAL) => temperatureUnits[unitSystem].long;
const displayShortPressureUnit: FormatterFn = (unitSystem: UnitSystem = UnitSystem.IMPERIAL) => pressureUnits[unitSystem].short;
const displayLongPressureUnit: FormatterFn = (unitSystem: UnitSystem = UnitSystem.IMPERIAL) => pressureUnits[unitSystem].long;
const displayShortFlowUnit: FormatterFn = (unitSystem: UnitSystem = UnitSystem.IMPERIAL) => flowUnits[unitSystem].short;
const displayLongFlowUnit: FormatterFn = (unitSystem: UnitSystem = UnitSystem.IMPERIAL) => flowUnits[unitSystem].long;
const displayShortHumidityUnit: FormatterFn = (_: UnitSystem = UnitSystem.IMPERIAL) => humidityUnits.short;
const displayLongHumidityUnit: FormatterFn = (_: UnitSystem = UnitSystem.IMPERIAL) => humidityUnits.long;
const displayShortBatteryUnit: FormatterFn = (_: UnitSystem = UnitSystem.IMPERIAL) => batteryUnits.short;
const displayLongBatteryUnit: FormatterFn = (_: UnitSystem = UnitSystem.IMPERIAL) => batteryUnits.long;

export const UnitFormat = {
  displayShortVolumeUnit,
  displayLongVolumeUnit,
  displayShortTempUnit,
  displayLongTempUnit,
  displayShortPressureUnit,
  displayLongPressureUnit,
  displayShortFlowUnit,
  displayLongFlowUnit,
  displayShortHumidityUnit,
  displayLongHumidityUnit,
  displayShortBatteryUnit,
  displayLongBatteryUnit,
};

export const UnitConverter = {
  convertFromGallons,
  convertToGallons,
  convertFromPSI,
  convertFromFahrenheit
};

export default UnitSystem;